import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, LogOut, Search } from "lucide-react";

export default function LessonsHub() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    api.get("/lessons").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const categories = useMemo(() => [...new Set((data?.lessons || []).map((l) => l.category).filter(Boolean))], [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.lessons || [])
      .filter((l) => projectFilter === "all" || l.projectId === projectFilter)
      .filter((l) => categoryFilter === "all" || l.category === categoryFilter)
      .filter((l) => !q || [l.description, l.impact, l.recommendation, l.category, l.projectName].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [data, query, projectFilter, categoryFilter]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/projects" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Projects
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium">{user?.name}</span>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-600"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="lessons-hub-page">
        <PageHeader overline={`${user?.tenantName} · Organisation-wide knowledge base`} title="Lessons Learned Log" />
        <div className="flex flex-wrap items-center gap-3 mt-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="lessons-search-input" className="pl-8 h-9" placeholder="Search lessons…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger data-testid="lessons-project-filter" className="w-56 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {(data?.projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="lessons-category-filter" className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{filtered.length} lesson{filtered.length === 1 ? "" : "s"}</span>
        </div>

        <div className="border border-border bg-card overflow-auto">
          <table className="w-full data-table text-sm">
            <thead><tr><th>Project</th><th>Category</th><th>Lesson</th><th>Impact</th><th>Recommendation</th></tr></thead>
            <tbody>
              {data === null && <tr><td colSpan={5} className="text-center text-muted-foreground py-8">Loading…</td></tr>}
              {data && filtered.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-8">No lessons match.</td></tr>}
              {filtered.map((l) => (
                <tr key={l.id} data-testid="lesson-row">
                  <td className="whitespace-nowrap"><Link to={`/projects/${l.projectId}/lessons_learned`} className="text-[#1E6FE8] hover:underline">{l.projectName}</Link></td>
                  <td className="whitespace-nowrap">{l.category || "—"}</td>
                  <td className="max-w-sm">{l.description}</td>
                  <td className="max-w-xs">{l.impact || "—"}</td>
                  <td className="max-w-sm">{l.recommendation || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
